import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { useLang } from '../hooks/useLang';
import './Chat.css';

export default function Chat() {
  const { bookingId } = useParams();
  const { user, profile } = useAuth();
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [booking, setBooking] = useState(null);
  const [otherPerson, setOtherPerson] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetchBookingAndMessages();

    // Subscribe to realtime new messages
    const channel = supabase
      .channel('messages:' + bookingId)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: 'booking_id=eq.' + bookingId },
        (payload) => {
          setMessages(prev => [...prev, payload.new]);
          markMessagesRead();
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [bookingId]);

  // Auto scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function fetchBookingAndMessages() {
    setLoading(true);

    // Fetch booking with trip and both user profiles
    // Use explicit FK aliases to avoid ambiguity on bookings.profiles
    const { data: bookingData } = await supabase
      .from('bookings')
      .select('*, sender:profiles!sender_id(id, full_name), trips(origin_city, destination_city, departure_date, origin_country, destination_country, transporter_id, transporter:profiles!transporter_id(id, full_name))')
      .eq('id', bookingId)
      .maybeSingle();

    if (bookingData) {
      setBooking(bookingData);
      const isSender = bookingData.sender_id === user.id;
      if (isSender) {
        // sender is chatting → other person is the transporter
        setOtherPerson(bookingData.trips?.transporter);
      } else {
        // transporter is chatting → other person is the sender
        setOtherPerson(bookingData.sender);
      }
    }

    // Fetch messages
    const { data: msgs } = await supabase
      .from('messages')
      .select('*, profiles!sender_id(full_name)')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: true });

    setMessages(msgs || []);
    setLoading(false);
    markMessagesRead();
  }

  async function markMessagesRead() {
    await supabase
      .from('messages')
      .update({ read: true })
      .eq('booking_id', bookingId)
      .neq('sender_id', user.id);
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    const content = text.trim();
    setText('');

    await supabase.from('messages').insert({
      booking_id: bookingId,
      sender_id: user.id,
      content,
    });
    setSending(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  }

  function groupMessages(msgs) {
    // Group consecutive messages from the same sender
    const groups = [];
    msgs.forEach((msg, i) => {
      const prev = msgs[i - 1];
      if (prev && prev.sender_id === msg.sender_id) {
        groups[groups.length - 1].messages.push(msg);
      } else {
        groups.push({ sender_id: msg.sender_id, messages: [msg] });
      }
    });
    return groups;
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!booking) return <div className="page"><div className="container">{t('chat.bookingNotFound')}</div></div>;

  const grouped = groupMessages(messages);
  const trip = booking.trips;

  return (
    <div className="chat-page">
      {/* HEADER */}
      <div className="chat-header">
        <div className="chat-header-inner">
          <Link to="/dashboard" className="chat-back">←</Link>
          <div className="chat-header-avatar">
            {otherPerson?.full_name?.[0] || '?'}
          </div>
          <div className="chat-header-info">
            <div className="chat-header-name">{otherPerson?.full_name || 'Unknown'}</div>
            <div className="chat-header-route">
              {trip?.origin_city} → {trip?.destination_city} · {trip?.departure_date && format(new Date(trip.departure_date), 'MMM d, yyyy')}
            </div>
          </div>
          <div className="chat-header-status">
            <span className="chat-online-dot" />
          </div>
        </div>
      </div>

      {/* BOOKING SUMMARY BAR */}
      <div className="chat-booking-bar">
        <div className="chat-booking-bar-inner">
          <span className="chat-booking-detail">📦 {booking.kg_booked} {t('chat.kg')}</span>
          <span className="chat-booking-sep">·</span>
          <span className="chat-booking-detail">€{booking.total_price?.toFixed(2)} {t('chat.total')}</span>
          <span className="chat-booking-sep">·</span>
          <span className={'badge ' + (
            booking.status === 'confirmed' ? 'badge-green' :
            booking.status === 'pending' ? 'badge-yellow' :
            booking.status === 'delivered' ? 'badge-green' : 'badge-gray'
          )}>{booking.status}</span>
        </div>
      </div>

      {/* MESSAGES */}
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">
            <div className="chat-empty-icon">💬</div>
            <p>{t('chat.noMessages')}</p>
          </div>
        )}

        {grouped.map((group, gi) => {
          const isMe = group.sender_id === user.id;
          return (
            <div key={gi} className={'chat-group ' + (isMe ? 'chat-group-me' : 'chat-group-them')}>
              {!isMe && (
                <div className="chat-group-avatar">
                  {otherPerson?.full_name?.[0] || '?'}
                </div>
              )}
              <div className="chat-group-bubbles">
                {group.messages.map((msg, mi) => (
                  <div key={msg.id} className={'chat-bubble ' + (isMe ? 'chat-bubble-me' : 'chat-bubble-them')}>
                    <span className="chat-bubble-text">{msg.content}</span>
                    {mi === group.messages.length - 1 && (
                      <span className="chat-bubble-time">
                        {format(new Date(msg.created_at), 'HH:mm')}
                        {isMe && <span className="chat-read-tick">{msg.read ? ' ✓✓' : ' ✓'}</span>}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="chat-input-bar">
        <form className="chat-input-form" onSubmit={sendMessage}>
          <textarea
            className="chat-input"
            placeholder={t('chat.placeholder')}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={sending}
          />
          <button
            type="submit"
            className="chat-send-btn"
            disabled={sending || !text.trim()}
          >
            {sending ? '...' : '➤'}
          </button>
        </form>
      </div>
    </div>
  );
}